import * as React from "react";
import {
  Box,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckboxList from "../common/CheckboxList";

interface FilterOption {
  id: string;
  name: string;
}

interface Props {
  anchorEl: null | HTMLElement;
  setAnchorEl: (el: null | HTMLElement) => void;
  payerProgramOptions: FilterOption[];
  creatorOptions: FilterOption[];
  signerOptions: FilterOption[];
  selectedPayerPrograms: string[];
  setSelectedPayerPrograms: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCreators: string[];
  setSelectedCreators: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSigners: string[];
  setSelectedSigners: React.Dispatch<React.SetStateAction<string[]>>;
  disabled?: boolean;
}

const SignedInterventionsBillingAdvancedFilters: React.FC<Props> = ({
  anchorEl,
  setAnchorEl,
  payerProgramOptions,
  creatorOptions,
  signerOptions,
  selectedPayerPrograms,
  setSelectedPayerPrograms,
  selectedCreators,
  setSelectedCreators,
  selectedSigners,
  setSelectedSigners,
  disabled = false,
}) => {
  const open = Boolean(anchorEl);

  const [payerExpanded, setPayerExpanded] = React.useState(true);
  const [creatorExpanded, setCreatorExpanded] = React.useState(true);
  const [signerExpanded, setSignerExpanded] = React.useState(true);

  const totalSelected =
    selectedPayerPrograms.length + selectedCreators.length + selectedSigners.length;

  const clearAllFilters = () => {
    setSelectedPayerPrograms([]);
    setSelectedCreators([]);
    setSelectedSigners([]);
    setAnchorEl(null);
  };

  const accordionSx = {
    boxShadow: "none",
    border: "none",
    marginTop: "12px",
    "&.Mui-expanded": { marginTop: "12px" },
    "&:before": { display: "none" },
  };

  const summaryContentSx = {
    padding: 0,
    minHeight: "20px",
    "&.MuiAccordionSummary-root": { minHeight: "20px" },
    "&.MuiAccordionSummary-gutters": { padding: 0, minHeight: "20px" },
    "&.Mui-expanded": { minHeight: "20px" },
    "& .MuiAccordionSummary-content": { margin: 0, alignItems: "center", minHeight: "20px" },
    "& .MuiAccordionSummary-contentGutters": { marginBottom: "10px", minHeight: "20px" },
    "& .MuiAccordionSummary-content.Mui-expanded": { margin: 0, minHeight: "20px", alignItems: "center" },
  };

  const detailsSx = {
    padding: 0,
    backgroundColor: "#F8F8F8",
    p: "20px 20px 12px 16px",
  };

  const renderSection = (
    label: string,
    expanded: boolean,
    toggle: () => void,
    options: FilterOption[],
    checked: string[],
    setChecked: React.Dispatch<React.SetStateAction<string[]>>
  ) => (
    <Accordion expanded={expanded} onChange={toggle} sx={accordionSx}>
      <AccordionSummary expandIcon={null} sx={summaryContentSx}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", p: "0 20px 0 12px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <ArrowDropDownIcon />
          </Box>
          <p className="collapse-list-header">{label}</p>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>
        {options.length === 0 ? (
          <p style={{ fontSize: 10, color: "#A29D9B", margin: 0 }}>No options available</p>
        ) : (
          <CheckboxList
            items={options.map((o) => ({ id: o.id, label: <div>{o.name}</div> }))}
            checkedItems={checked}
            setCheckedItems={setChecked}
            labelField="label"
            disabled={disabled}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Popover
      id="signed-interventions-filters"
      open={open}
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="popover-filter-container">
        <div className="header-wrapper">
          <div className="header">Filters</div>
          {totalSelected > 0 && (
            <button onClick={clearAllFilters} className="clear-all">
              <svg width="9" height="9" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect y="7.99902" width="8" height="8" rx="1" transform="rotate(-90 0 7.99902)" fill="#FF890A" />
                <path
                  d="M2.13699 1.66585L1.66699 2.13585L3.53033 3.99919L1.66699 5.86252L2.13699 6.33252L4.00033 4.46919L5.86366 6.33252L6.33366 5.86252L4.47033 3.99919L6.33366 2.13585L5.86366 1.66585L4.00033 3.52919L2.13699 1.66585Z"
                  fill="white"
                />
              </svg>
              <span>Clear All</span>
            </button>
          )}
        </div>

        {renderSection(
          "Payor Program",
          payerExpanded,
          () => setPayerExpanded(!payerExpanded),
          payerProgramOptions,
          selectedPayerPrograms,
          setSelectedPayerPrograms
        )}
        {renderSection(
          "Creator",
          creatorExpanded,
          () => setCreatorExpanded(!creatorExpanded),
          creatorOptions,
          selectedCreators,
          setSelectedCreators
        )}
        {renderSection(
          "Signer",
          signerExpanded,
          () => setSignerExpanded(!signerExpanded),
          signerOptions,
          selectedSigners,
          setSelectedSigners
        )}
      </div>
    </Popover>
  );
};

export default SignedInterventionsBillingAdvancedFilters;
